import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/api";

const fmtWeek = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

function Delta({ label, data, testId }) {
  const diff = data.this_week - data.last_week;
  const pct = data.last_week ? Math.round((diff / data.last_week) * 100) : null;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const tone = diff > 0 ? "text-emerald-600 bg-emerald-50" : diff < 0 ? "text-red-600 bg-red-50" : "text-neutral-500 bg-neutral-100";
  return (
    <div className="soft-card p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{label} · this week</p>
      <div className="flex items-end justify-between mt-2">
        <p className="text-2xl font-bold tabular">{data.this_week}</p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone}`}>
          <Icon size={12} /> {diff > 0 ? "+" : ""}{diff}{pct !== null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""}
        </span>
      </div>
      <p className="text-[11px] text-neutral-400 mt-1">vs {data.last_week} last week</p>
    </div>
  );
}

function ChartCard({ title, data, bars, testId }) {
  return (
    <div className="soft-card p-4" data-testid={testId}>
      <p className="text-sm font-semibold text-neutral-900 mb-3">{title}</p>
      <div className="h-48">
        <ResponsiveContainer width="100%" height={192} initialDimension={{ width: 320, height: 192 }}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0ee" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a3a3a3" }} tickLine={false} axisLine={false} interval={1} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#a3a3a3" }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(14,140,90,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 12 }} />
            {bars.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />}
            {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} stackId="a" fill={b.color} radius={b.top ? [6, 6, 0, 0] : 0} maxBarSize={28} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminActivityCharts() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/analytics/weekly?weeks=12"); setData(data); })(); }, []);
  if (!data) return <p className="text-sm text-neutral-500 mt-8">Loading trends…</p>;
  const weeks = data.weeks.map((w) => ({ ...w, label: fmtWeek(w.week_start) }));
  return (
    <section className="mt-8" data-testid="admin-activity-charts">
      <h2 className="text-lg font-bold text-neutral-900">Weekly trends <span className="text-sm font-normal text-neutral-500">· last 12 weeks</span></h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <Delta label="Sign-ups" data={data.summary.signups} testId="trend-delta-signups" />
        <Delta label="Document uploads" data={data.summary.uploads} testId="trend-delta-uploads" />
        <Delta label="New interests" data={data.summary.interests} testId="trend-delta-interests" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
        <ChartCard title="New sign-ups" data={weeks} testId="trend-chart-signups" bars={[{ key: "investors", name: "Investors", color: "#3b82f6" }, { key: "developers", name: "Developers", color: "#0E8C5A", top: true }]} />
        <ChartCard title="Document uploads" data={weeks} testId="trend-chart-uploads" bars={[{ key: "uploads", name: "Uploads", color: "#E8B931", top: true }]} />
        <ChartCard title="New interests" data={weeks} testId="trend-chart-interests" bars={[{ key: "interests", name: "Interests", color: "#0ea5e9", top: true }]} />
      </div>
    </section>
  );
}
